import {
  APPOINTMENT_RATING_QUESTIONNAIRE_ID,
  COACHING_RATING_QUESTIONNAIRE_ID
} from "src/constants/misc";

/**
 * Program group that holds every appointment rating task (coaching calls plus
 * dietitian / specialist / pharmacist visits). The backend creates one task per
 * rateable appointment in this group, keyed by `additionalData.appointmentId`,
 * with a per-type slug. We match on the group rather than the slug so the rating
 * popup opens for all current and future appointment types.
 */
export const APPOINTMENT_RATING_GROUP = "rate-appointment";

export enum AppointmentRatingSlug {
  COACHING = "coaching",
  DIETITIAN = "dietitian",
  SPECIALIST = "specialist",
  PHARMACIST = "pharmacist"
}

export interface AppointmentRatingConfig {
  /** Custom questionnaire rendered for this appointment type. */
  questionnaireId: string;
  /** `additionalData` key the backend uses for the provider's name. */
  nameDataKey: string;
  /** `additionalData` key the backend uses for the provider's user id. */
  userIdDataKey: string;
  /** Questionnaire variable the provider's name is passed to. */
  nameHiddenField: string;
  /** Questionnaire variable the provider's user id (avatar) is passed to. */
  userIdHiddenField: string;
}

/**
 * Coaching keeps the original `coaching*` keys/variables for backwards
 * compatibility; every other appointment type shares the generic `staff*`
 * contract used by the appointment rating questionnaire.
 */
const COACHING_CONFIG: AppointmentRatingConfig = {
  questionnaireId: COACHING_RATING_QUESTIONNAIRE_ID,
  nameDataKey: "coachingName",
  userIdDataKey: "coachingUserId",
  nameHiddenField: "coaching_name",
  userIdHiddenField: "coaching_user_id"
};

const STAFF_CONFIG: AppointmentRatingConfig = {
  questionnaireId: APPOINTMENT_RATING_QUESTIONNAIRE_ID,
  nameDataKey: "staffName",
  userIdDataKey: "staffUserId",
  nameHiddenField: "staff_name",
  userIdHiddenField: "staff_user_id"
};

export const getAppointmentRatingConfig = (
  slug: string
): AppointmentRatingConfig =>
  slug === AppointmentRatingSlug.COACHING ? COACHING_CONFIG : STAFF_CONFIG;
